import { makeCurveProjector, projectPointToCurve, smoothAnchorsThrough } from '../bezier-geometry';
import { DEFAULT_CLUSTER, DEFAULT_POPCORN } from '../cluster-geometry';
import {
    type BezierCurveGuide,
    clampAngleToArc,
    type ConcentricArcsGuide,
    type ConcentricCirclesGuide,
    type Guide,
    type GuideId,
    type LineGuide,
    type RingId,
} from '../guide-types';
import { clampedSegmentParam, resolveGuideSnapBinding } from '../snap';
import type { Space, SpaceId } from '../space-types';
import {
    type Base,
    type GuideSnap,
    isChainSpaceType,
    type SnapBinding,
    type Tbc,
    type TbcId,
    type TbcPoint,
    type Top,
    type TopAttachSnap,
    type TopSnap,
} from '../tbc-types';
import { nodeRefKey, type VerticalLink, type VerticalLinkId, type VerticalNodeRef } from '../vertical-link-types';
import { legacyInstanceCount, legacyInstanceViewModel, legacySpaceAngleAt, legacySpacePointAt, legacySpaceRadius } from './geometry';
import {
    legacyBaseId,
    legacySpaceAnchorId,
    legacySpaceGuideId,
    legacySpaceId,
    legacySpaceRingId,
    legacyStitchId,
    legacyTopId,
} from './ids';
import type {
    LegacyArrayTbc,
    LegacyArrayTbcId,
    WideBase,
    WideSnapBinding,
    WideTbc,
    WideTop,
    WideTopSnap,
    WideVerticalLink,
    WideVerticalNodeRef,
} from './types';

// SP-125. Turns a document's stitch arrays into ordinary stitches, one per
// instance the renderer drew, and rewrites what referred to an array. Every
// replica runs this on the same data and must land on the same result, and
// the Yjs write-back diffs its output by object identity, so it is:
//
//  - pure and deterministic: the output depends on the input alone. Where the
//    order of iteration could change a decision, ids go in code-unit order
//    (never `localeCompare`), as in repair-references.ts;
//  - copy-on-write: an entity it doesn't change is passed through as the same
//    object, a changed one is a new object, and nothing in the input is
//    mutated (Maps included: the output Maps are new);
//  - total on schema-valid input: it doesn't throw (see `instanceViewModels`
//    for the one case the frozen geometry throws on).
//
// Phases, in order:
//  1. Explode. Arrays in map order, instances by index. Instance i of array A
//     becomes the stitch `legacyStitchId(A, i)`, unless a stitch of that id
//     already exists (a replica converted A, then an old peer re-sent it):
//     the existing stitch wins and instance i isn't created again.
//  2. Stitch order. The exploded stitches come first (arrays drew under every
//     stitch), then the existing stitches in their order.
//  3. Decide each reference to an array, on that whole stitch set:
//      - `array-top` (a base on an instance top) becomes `tbc-top` on the
//        instance's stitch (`remapArrayTop`);
//      - `array-base` (a top pinned under an instance base) is inverted: the
//        instance's base rides that top (`invertArrayBasePins`);
//      - `array-space` (a base worked into an array's chain space) is bound to
//        the target guide of a Space made from the space curve
//        (`convertArraySpaces`), which adds that guide and Space.
//     The derived ids don't need the array, so a reference that arrives after
//     its array was converted still finds the converted stitch or Space.
//     Anything a reference can't be resolved to is let go where it is: the
//     snap becomes null, the point is kept.
//  4. Narrow. Each stitch is rewritten once with those decisions
//     (`narrowStitch`); no snap that names an array survives, which the types
//     check.
//  5. Vertical links: an `array` end becomes instance 0's stitch.
//  6. Turning chains: an array id becomes its chain-family instance ids, the
//     array there or (a late mark of a converted array) gone.

/** The converter's input: a document's collections as stored, arrays and all. */
export type LegacyDocMaps = {
    stitches: ReadonlyMap<TbcId, WideTbc>;
    guides: ReadonlyMap<GuideId, Guide>;
    arrayTbcs: ReadonlyMap<LegacyArrayTbcId, LegacyArrayTbc>;
    verticalLinks: ReadonlyMap<VerticalLinkId, WideVerticalLink>;
    spaces: ReadonlyMap<SpaceId, Space>;
    turningChains: ReadonlySet<string>;
};

/** The converter's output: no arrays, and nothing that refers to one. */
export type ConvertedDocMaps = {
    stitches: Map<TbcId, Tbc>;
    guides: Map<GuideId, Guide>;
    verticalLinks: Map<VerticalLinkId, VerticalLink>;
    spaces: Map<SpaceId, Space>;
    turningChains: Set<string>;
};

type ArrayTopSnap = Extract<WideSnapBinding, { kind: 'array-top' }>;
type ArraySpaceSnap = Extract<WideSnapBinding, { kind: 'array-space' }>;
type SpaceTarget = NonNullable<Space['target']>;

/** Which snap kinds name an array: exactly the kinds the narrowed
 *  `SnapBinding` leaves out, which the type forces (a kind the package adds
 *  fails to compile here until the narrowed types decide on it). Top snap
 *  kinds are a subset of base snap kinds, so this answers for both. */
const NAMES_AN_ARRAY: { readonly [K in WideSnapBinding['kind']]: K extends SnapBinding['kind'] ? false : true } = {
    'guide-line': false,
    'guide-circle': false,
    'guide-bezier': false,
    'tbc-top': false,
    'array-top': true,
    'array-base': true,
    'array-space': true,
};

const namesAnArray = (snap: WideSnapBinding | WideTopSnap | null): boolean => snap != null && NAMES_AN_ARRAY[snap.kind];

const isNarrowSnap = (snap: WideSnapBinding): snap is SnapBinding => !NAMES_AN_ARRAY[snap.kind];
const isNarrowTopSnap = (snap: WideTopSnap): snap is TopSnap => !NAMES_AN_ARRAY[snap.kind];
const isNarrowBase = (b: WideBase): b is Base => b.snap == null || isNarrowSnap(b.snap);
const isNarrowTop = (top: WideTop): top is Top => top.snap == null || isNarrowTopSnap(top.snap);
const isNarrowTbc = (t: WideTbc): t is Tbc => t.bases.every(isNarrowBase) && t.tops.every(isNarrowTop);

/** Code-unit order. Never `localeCompare`: its result depends on the device. */
const byId = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

const clamp01 = (f: number): number => Math.max(0, Math.min(1, f));

/** A copy: the geometry hands out shared, cached point objects. */
const copyPoint = (p: TbcPoint): TbcPoint => ({ x: p.x, y: p.y });

/** True when the document holds anything the converter would change: an
 *  array, a snap onto one, a vertical link to one, or a turning chain that
 *  names a converted array (`namesConvertedArray`). */
export function hasLegacyArrayContent(doc: LegacyDocMaps): boolean {
    if (doc.arrayTbcs.size > 0) return true;
    for (const t of doc.stitches.values()) {
        if (t.bases.some(b => namesAnArray(b.snap)) || t.tops.some(top => namesAnArray(top.snap))) return true;
    }
    for (const link of doc.verticalLinks.values()) {
        if (!isNarrowLink(link)) return true;
    }
    for (const id of doc.turningChains) {
        if (namesConvertedArray(id, (stitchId) => doc.stitches.has(stitchId))) return true;
    }
    return false;
}

/** True when `id`, an id with no array of its own in the document, names an
 *  array converted earlier: it is no stitch, while its instance 0 stitch
 *  (`legacyStitchId(id, 0)`) is. A turning chain an old peer marked after a
 *  replica converted the array names one, and is remapped like a mark made
 *  before (`remapTurningChains`). Derived ids can't collide with existing
 *  ones (see ids.ts), so nothing else answers yes. */
export function namesConvertedArray(id: string, isStitch: (id: TbcId) => boolean): boolean {
    return !isStitch(id) && isStitch(legacyStitchId(id, 0));
}

/** The document with every array exploded into stitches and every reference to
 *  an array rewritten. See the file header for the rules. */
export function explodeLegacyArrays(doc: LegacyDocMaps): ConvertedDocMaps {
    // Every stitch of the output, still wide: the exploded ones first, then the
    // existing ones in their order (explodeArrays skips the ids they hold).
    const wide = new Map<TbcId, WideTbc>(explodeArrays(doc));
    for (const [id, t] of doc.stitches) wide.set(id, t);

    const arrayTop = (childId: TbcId, snap: ArrayTopSnap): TopAttachSnap | null => remapArrayTop(childId, snap, wide);
    const pins = invertArrayBasePins(wide, arrayTop);
    const guides = new Map(doc.guides);
    const spaces = new Map(doc.spaces);
    const spaceBase = convertArraySpaces(doc, wide, guides, spaces);

    const stitches = new Map<TbcId, Tbc>();
    for (const [id, t] of wide) stitches.set(id, narrowStitch(id, t, { arrayTop, pins, spaceBase }));

    return {
        stitches,
        guides,
        verticalLinks: remapVerticalLinks(doc.verticalLinks, (id) => stitches.has(id)),
        spaces,
        turningChains: remapTurningChains(doc, stitches),
    };
}

// ---- 1. Instances ----------------------------------------------------------

/** The new stitches, arrays in map order and instances by index, skipping an
 *  instance whose id is already a stitch. Derived ids use the Map key, which is
 *  what references name. */
function explodeArrays(doc: LegacyDocMaps): Map<TbcId, Tbc> {
    const out = new Map<TbcId, Tbc>();
    for (const [arrayId, a] of doc.arrayTbcs) {
        instanceViewModels(a).forEach((vm, i) => {
            const id = legacyStitchId(arrayId, i);
            if (doc.stitches.has(id)) return;
            out.set(id, instanceStitch(arrayId, a, i, vm));
        });
    }
    return out;
}

/** The view model of every instance the renderer drew (`legacyInstanceCount`).
 *
 *  A line or bezier array with a fractional `count` and `autoWidth` throws in
 *  the frozen geometry, as it did in the renderer, which never drew one. The
 *  converter must not throw on stored data, so such an array is placed as if
 *  its count were the floor: the whole array is recomputed, not just the
 *  instance that threw, so its instances stay evenly spaced. Everything else,
 *  fractional counts included, is placed exactly where the renderer drew it.
 *  If the recomputed array throws too, that propagates and the load fails, the
 *  stored document untouched: content is never dropped silently. */
function instanceViewModels(a: LegacyArrayTbc): WideTbc[] {
    const n = legacyInstanceCount(a);
    const all = (array: LegacyArrayTbc): WideTbc[] => Array.from({ length: n }, (_, i) => legacyInstanceViewModel(array, i));
    try {
        return all(a);
    } catch {
        return all({ ...a, count: n });
    }
}

/** Instance `i` of array `a` as an ordinary stitch (spec, "Per-instance
 *  mapping"): one base and one top at the drawn points, with the drawn width,
 *  colour and stroke. The height is already in the points, so no
 *  `heightScale`. The array's round goes on the top, its order on instance 0. */
function instanceStitch(arrayId: LegacyArrayTbcId, a: LegacyArrayTbc, i: number, vm: WideTbc): Tbc {
    return {
        id: legacyStitchId(arrayId, i),
        type: vm.type,
        bases: [{ id: legacyBaseId(arrayId, i), point: copyPoint(vm.bases[0].point), snap: null }],
        tops: [{
            id: legacyTopId(arrayId, i),
            point: copyPoint(vm.tops[0].point),
            snap: null,
            ...(a.round != null ? { userRound: a.round } : {}),
        }],
        ...(vm.widthScale != null ? { widthScale: vm.widthScale } : {}),
        ...(i === 0 && a.userOrder != null ? { userOrder: a.userOrder } : {}),
        ...(vm.color != null ? { color: vm.color } : {}),
        ...(vm.strokeWidthScale != null ? { strokeWidthScale: vm.strokeWidthScale } : {}),
        ...(vm.strokeCap != null ? { strokeCap: vm.strokeCap } : {}),
        // The stitch schema requires a spec for these, and array instances never
        // had one: the default (spec visible difference 7). A copy, so no
        // document shares the module's object.
        ...(vm.type === 'cluster' ? { cluster: { ...DEFAULT_CLUSTER } } : {}),
        ...(vm.type === 'popcorn' ? { popcorn: { ...DEFAULT_POPCORN } } : {}),
    };
}

// ---- 3a. `array-top` -------------------------------------------------------

/** The `tbc-top` snap on the instance's stitch for a base of stitch `childId`
 *  on an instance top, or `null` when the output has no such stitch carrying
 *  that top: an index the array never drew, an array that is gone with no
 *  converted stitch left, or a converted stitch whose top was since replaced.
 *  Also `null` when that stitch is the child itself (a stitch kept by
 *  "existing id wins" on its own index): no stitch rides its own top. */
function remapArrayTop(childId: TbcId, snap: ArrayTopSnap, wide: ReadonlyMap<TbcId, WideTbc>): TopAttachSnap | null {
    const { arrayId, index, side } = snap;
    const parentId = legacyStitchId(arrayId, index);
    const topId = legacyTopId(arrayId, index);
    if (parentId === childId) return null;
    if (wide.get(parentId)?.tops.some(top => top.id === topId) !== true) return null;
    return { kind: 'tbc-top', parentId, topId, ...(side != null ? { side } : {}) };
}

// ---- 3b. `array-base` ------------------------------------------------------

/** Instance base snaps by stitch and base index: what each `array-base` pin
 *  inverts into. A top T of stitch S pinned under instance i's base becomes
 *  instance i's base riding T (`tbc-top {S, T}`, on T's default side); every
 *  pinning top lets go of its pin, inverted or not (`narrowTop`).
 *
 *  A pin inverts only onto a free base: the instance's stitch must be in the
 *  output with its derived base id, and that base unattached. A stitch kept
 *  by "existing id wins" may have been attached since (perhaps already to T),
 *  and that attachment stands.
 *
 *  One pin per instance base, as repair keeps it: stitches by id, then their
 *  tops by id, both in code-unit order; the first pin seen owns the base.
 *
 *  No pin inverts into an attachment cycle. Pins are taken in instance id
 *  order, and one whose S already leads (through the bases' parent tops) to
 *  instance i, or is instance i, is left uninverted. The simplest case is
 *  the spec's cycle rule: S's own base sits on instance i's top, so that
 *  attachment is kept and instance i's base stays free. */
function invertArrayBasePins(
    wide: ReadonlyMap<TbcId, WideTbc>,
    arrayTop: (childId: TbcId, snap: ArrayTopSnap) => TopAttachSnap | null,
): Map<TbcId, Map<number, TopAttachSnap>> {
    const owners = new Map<TbcId, { parentId: TbcId; topId: string; baseId: string }>();
    for (const parentId of [...wide.keys()].sort(byId)) {
        for (const top of [...wide.get(parentId)!.tops].sort((a, b) => byId(a.id, b.id))) {
            if (top.snap?.kind !== 'array-base') continue;
            const instanceId = legacyStitchId(top.snap.arrayId, top.snap.index);
            if (!owners.has(instanceId)) owners.set(instanceId, { parentId, topId: top.id, baseId: legacyBaseId(top.snap.arrayId, top.snap.index) });
        }
    }

    const parents = attachmentParents(wide, arrayTop);
    const pins = new Map<TbcId, Map<number, TopAttachSnap>>();
    for (const instanceId of [...owners.keys()].sort(byId)) {
        const { parentId, topId, baseId } = owners.get(instanceId)!;
        const instance = wide.get(instanceId);
        const k = instance?.bases.findIndex(b => b.id === baseId) ?? -1;
        if (instance == null || k < 0 || instance.bases[k].snap != null) continue;
        if (leadsTo(parents, parentId, instanceId)) continue;
        pins.set(instanceId, new Map([[k, { kind: 'tbc-top', parentId, topId }]]));
        parents.set(instanceId, [...(parents.get(instanceId) ?? []), parentId]);
    }
    return pins;
}

/** Each stitch's parents in the output before any pin is inverted: the
 *  stitches its bases ride, by `tbc-top` or by a remapped `array-top`. */
function attachmentParents(
    wide: ReadonlyMap<TbcId, WideTbc>,
    arrayTop: (childId: TbcId, snap: ArrayTopSnap) => TopAttachSnap | null,
): Map<TbcId, TbcId[]> {
    const parents = new Map<TbcId, TbcId[]>();
    for (const [id, t] of wide) {
        const of: TbcId[] = [];
        for (const { snap } of t.bases) {
            const attach = snap?.kind === 'tbc-top' ? snap : snap?.kind === 'array-top' ? arrayTop(id, snap) : null;
            if (attach != null) of.push(attach.parentId);
        }
        if (of.length > 0) parents.set(id, of);
    }
    return parents;
}

/** True when `from` is `to`, or rides it through any chain of parents. */
function leadsTo(parents: ReadonlyMap<TbcId, readonly TbcId[]>, from: TbcId, to: TbcId): boolean {
    const seen = new Set<TbcId>();
    const stack = [from];
    while (stack.length > 0) {
        const id = stack.pop()!;
        if (id === to) return true;
        if (seen.has(id)) continue;
        seen.add(id);
        stack.push(...(parents.get(id) ?? []));
    }
    return false;
}

// ---- 3c. `array-space` -----------------------------------------------------

/** How the bases worked into one array's space are bound. */
type SpaceRoute =
    /** To the guide made for the array's new Space, at the fraction they had. */
    | { route: 'exact'; snapAt: (f: number) => GuideSnap }
    /** To the target of the array's Space, converted earlier: projected there. */
    | { route: 'project'; target: SpaceTarget }
    /** Nowhere: let go where they are. */
    | { route: 'detach' };

/** Adds to `guides` and `spaces` the Space and target guide of every array a
 *  base was worked into, and returns how to rewrite such a base. Per array A
 *  named by an `array-space` snap:
 *
 *  - A Space with A's derived id already exists (A was converted before: A is
 *    gone, or an old peer re-sent it). It wins and nothing is made; each base
 *    is projected onto that Space's target, or let go if the target is gone.
 *  - A is absent: let go.
 *  - A has no chain-family instance (inconsistent but storable): no Space, and
 *    the bases are let go.
 *  - A guide already holds A's derived guide id (but no Space does): that
 *    guide wins, and with no Space to bind to the bases are let go.
 *  - Otherwise: a Space of A's chain-family instances, in index order, whose
 *    target is a new round-less guide on A's chain-space curve (`spaceCurve`).
 *    Each base goes to the point of that guide for its fraction.
 *
 *  A base bound to a guide is moved onto the point its snap resolves to, as
 *  the engine would place it, so the document holds no base off its guide.
 *  New guides and Spaces are added in array map order, after the existing
 *  ones. A chain already in an existing Space isn't claimed again. */
function convertArraySpaces(
    doc: LegacyDocMaps,
    wide: ReadonlyMap<TbcId, WideTbc>,
    guides: Map<GuideId, Guide>,
    spaces: Map<SpaceId, Space>,
): (b: WideBase, snap: ArraySpaceSnap) => Base {
    const bound = new Set<LegacyArrayTbcId>();
    for (const t of wide.values()) {
        for (const { snap } of t.bases) if (snap?.kind === 'array-space') bound.add(snap.arrayId);
    }
    const claimed = new Set<TbcId>([...doc.spaces.values()].flatMap(s => s.chainIds));

    const routes = new Map<LegacyArrayTbcId, SpaceRoute>();
    for (const [arrayId, a] of doc.arrayTbcs) {
        const spaceId = legacySpaceId(arrayId);
        const guideId = legacySpaceGuideId(arrayId);
        if (!bound.has(arrayId) || doc.spaces.has(spaceId) || doc.guides.has(guideId)) continue;
        const chainIds = Array.from({ length: legacyInstanceCount(a) }, (_, i) => legacyStitchId(arrayId, i)).filter(id => {
            const s = wide.get(id);
            return s != null && !claimed.has(id) && isChainSpaceType(s.type);
        });
        if (chainIds.length === 0) continue;
        const curve = spaceCurve(arrayId, a);
        guides.set(guideId, curve.guide);
        spaces.set(spaceId, { id: spaceId, chainIds, target: curve.ringId == null ? { guideId } : { guideId, ringId: curve.ringId } });
        routes.set(arrayId, { route: 'exact', snapAt: curve.snapAt });
    }
    for (const arrayId of bound) {
        if (routes.has(arrayId)) continue;
        const target = doc.spaces.get(legacySpaceId(arrayId))?.target;
        routes.set(arrayId, target != null ? { route: 'project', target } : { route: 'detach' });
    }

    return (b, snap) => {
        const r = routes.get(snap.arrayId) ?? { route: 'detach' };
        const binding = r.route === 'exact' ? r.snapAt(snap.f)
            : r.route === 'project' ? projectOntoTarget(b.point, r.target, guides)
            : null;
        const at = binding == null ? null : resolveGuideSnapBinding(binding, guides);
        return binding == null || at == null ? { ...b, snap: null } : { ...b, point: copyPoint(at), snap: binding };
    };
}

/** The round-less guide on array `a`'s chain-space curve, and the snap on it
 *  for fraction `f` of that curve:
 *
 *  - line: a `line` guide between the curve's ends; `t = f`. Exact.
 *  - circle / arc: one ring on the space radius (over the array's angles for
 *    an arc), at the angle the curve passes `f` (`legacySpaceAngleAt`), so the
 *    ring point is `legacySpacePointAt(a, f)` to the bit.
 *  - bezier: the offset of a bezier is no bezier, so a curve is fitted through
 *    evenly spaced samples of it (`fitSpaceCurve`), with derived anchor ids:
 *    16, doubled while the worst stray, measured at segment midpoints, is over
 *    `SPACE_FIT_TOLERANCE`, up to 256. `f` binds to the guide point nearest
 *    `legacySpacePointAt(a, f)`, projected as guide snapping projects. The
 *    tests pin that within 0.25 world units of a curve without a cusp; at a
 *    cusp or jump no smooth curve follows it, so the fit runs to the cap and
 *    may stray more there. */
function spaceCurve(arrayId: LegacyArrayTbcId, a: LegacyArrayTbc): { guide: Guide; ringId?: RingId; snapAt: (f: number) => GuideSnap } {
    const guideId = legacySpaceGuideId(arrayId);
    switch (a.shape) {
        case 'line': {
            const guide: LineGuide = { id: guideId, type: 'line', start: copyPoint(legacySpacePointAt(a, 0)), end: copyPoint(legacySpacePointAt(a, 1)) };
            return { guide, snapAt: f => ({ kind: 'guide-line', guideId, t: clamp01(f) }) };
        }
        case 'circle': {
            const circle = a;
            const ringId = legacySpaceRingId(arrayId);
            const guide: ConcentricCirclesGuide = {
                id: guideId,
                type: 'concentric-circles',
                center: copyPoint(circle.center),
                rings: [{ id: ringId, radius: legacySpaceRadius(circle) }],
            };
            return { guide, ringId, snapAt: f => ({ kind: 'guide-circle', guideId, ringId, angle: legacySpaceAngleAt(circle, f) }) };
        }
        case 'arc': {
            const arc = a;
            const ringId = legacySpaceRingId(arrayId);
            const guide: ConcentricArcsGuide = {
                id: guideId,
                type: 'concentric-arcs',
                center: copyPoint(arc.center),
                rings: [{ id: ringId, radius: legacySpaceRadius(arc), startAngle: arc.startAngle, endAngle: arc.endAngle }],
            };
            return { guide, ringId, snapAt: f => ({ kind: 'guide-circle', guideId, ringId, angle: legacySpaceAngleAt(arc, f) }) };
        }
        case 'bezier-curve': {
            const curve = a;
            const { guide, project } = fitSpaceCurve(arrayId, curve);
            return { guide, snapAt: f => ({ kind: 'guide-bezier', guideId, s: project(f) }) };
        }
        default: {
            const _exhaustive: never = a;
            return _exhaustive;
        }
    }
}

/** Sample counts for the bezier fit of a chain-space curve: it starts at the
 *  minimum and doubles while the fit strays more than the tolerance
 *  (`fitSpaceCurve`), never past the maximum. Every sample is an anchor saved
 *  into the document and walked by each snap query, so the cap is kept low. */
const SPACE_FIT_MIN_SAMPLES = 16;
const SPACE_FIT_MAX_SAMPLES = 256;
/** How far the fit may stray from the space curve between two samples, in
 *  world units. A margin under the 0.25 the tests hold re-projected bases to. */
const SPACE_FIT_TOLERANCE = 0.1;
/** A base is projected onto the fitted segment its fraction falls in and this
 *  many on either side. */
const SPACE_FIT_WINDOW = 2;

/** Fraction of sample `k` of `m`: open curves include both ends, closed ones
 *  go evenly round. Segment k of the fit runs from sample k to sample k+1
 *  (the last wraps to sample 0 when closed). */
const sampleFraction = (k: number, m: number, closed: boolean): number => (closed ? k / m : k / (m - 1));

/** How many segments an `m`-sample fit has: open, m − 1; closed, m. */
const segmentCount = (m: number, closed: boolean): number => (closed ? m : m - 1);

/** A bezier guide through `m` samples of the curve, with derived anchor ids. */
function sampledSpaceGuide(arrayId: LegacyArrayTbcId, a: LegacyArrayTbc & { shape: 'bezier-curve' }, m: number): BezierCurveGuide {
    const samples = Array.from({ length: m }, (_, k) => legacySpacePointAt(a, sampleFraction(k, m, a.closed)));
    return {
        id: legacySpaceGuideId(arrayId),
        type: 'bezier-curve',
        anchors: smoothAnchorsThrough(samples, a.closed, k => legacySpaceAnchorId(arrayId, k)),
        closed: a.closed,
    };
}

/** The fitted segment fraction `f` falls in, with SPACE_FIT_WINDOW on either
 *  side: clamped at an open curve's ends, wrapping round a closed one, in
 *  ascending order (the order whole-curve projection breaks ties in). */
function segmentsNear(f: number, m: number, closed: boolean): number[] {
    const n = segmentCount(m, closed);
    const k = Math.min(n - 1, Math.floor(clamp01(f) * n));
    const near = new Set<number>();
    for (let i = k - SPACE_FIT_WINDOW; i <= k + SPACE_FIT_WINDOW; i++) {
        if (closed) near.add(((i % n) + n) % n);
        else if (i >= 0 && i < n) near.add(i);
    }
    return [...near].sort((x, y) => x - y);
}

/** A bezier guide fitted to a bezier array's chain-space curve, and the guide
 *  `s` for fraction `f` of that curve. The offset of a bezier is no bezier, so
 *  this is a smooth curve (`smoothAnchorsThrough`) through samples of it.
 *
 *  How far a fit strays: per segment, the curve's point halfway in f between
 *  the segment's two samples (the fit passes through the samples themselves),
 *  and its distance to that segment; the fit's worst stray is the largest.
 *  That is never less than the point's distance to the whole guide, so a
 *  point measured is never closer than it seems.
 *
 *  How many samples: M starts at SPACE_FIT_MIN_SAMPLES and doubles while the
 *  worst stray is over SPACE_FIT_TOLERANCE, up to SPACE_FIT_MAX_SAMPLES; the
 *  first fit within the tolerance is kept, else the one at the cap. The stray
 *  needn't halve at every doubling (on a smooth curve it can fall slowly at
 *  first and fast once M is large enough), so no doubling is judged by how
 *  much it helped. Where the space curve has a cusp or a jump (a cusp in the
 *  array's own curve flips its normal, so the offset jumps across it; a tight
 *  turn folds the offset over), no smooth curve follows it, so the fit runs to
 *  the cap and may still stray more than the 0.25 the tests hold other bases
 *  to near that point. That case isn't held to it. Every step depends on the
 *  array alone, so every replica picks the same M.
 *
 *  `s` for `f`: the guide point nearest `legacySpacePointAt(a, f)`, as guide
 *  snapping projects (`projectPointToCurve`), but looked for only on the
 *  segments around where `f` falls (`segmentsNear`), so the cost per base
 *  doesn't grow with M. The fit follows the curve, so that's where the nearest
 *  point is, and there the answer is the whole-curve one to the bit. */
function fitSpaceCurve(
    arrayId: LegacyArrayTbcId,
    a: LegacyArrayTbc & { shape: 'bezier-curve' },
): { guide: BezierCurveGuide; project: (f: number) => number } {
    const fitWith = (m: number) => {
        const guide = sampledSpaceGuide(arrayId, a, m);
        const projector = makeCurveProjector(guide);
        const n = segmentCount(m, a.closed);
        let worst = 0;
        for (let k = 0; k < n; k++) {
            const p = legacySpacePointAt(a, (k + 0.5) / n);
            // No point only when the fit has no length: the curve is one point.
            const on = projector(p, [k])?.point ?? p;
            worst = Math.max(worst, Math.hypot(on.x - p.x, on.y - p.y));
        }
        return { m, guide, projector, worst };
    };

    let fit = fitWith(SPACE_FIT_MIN_SAMPLES);
    while (fit.worst > SPACE_FIT_TOLERANCE && fit.m < SPACE_FIT_MAX_SAMPLES) fit = fitWith(fit.m * 2);
    const { m, guide, projector } = fit;
    return { guide, project: f => projector(legacySpacePointAt(a, f), segmentsNear(f, m, a.closed))?.s ?? 0 };
}

/** The snap at the point of a Space's target nearest `p`, clamped to the
 *  guide's ends as the engine's project-onto-guide clamps, or `null` when the
 *  target is gone or names no single curve (a ring that isn't there, a ring on
 *  a guide that has none, or a concentric guide without a ring). */
function projectOntoTarget(p: TbcPoint, target: SpaceTarget, guides: ReadonlyMap<GuideId, Guide>): GuideSnap | null {
    const guide = guides.get(target.guideId);
    if (guide == null) return null;
    switch (guide.type) {
        case 'line':
            if (target.ringId != null) return null;
            return { kind: 'guide-line', guideId: guide.id, t: clampedSegmentParam(p, guide.start, guide.end) };
        case 'bezier-curve':
            if (target.ringId != null) return null;
            return { kind: 'guide-bezier', guideId: guide.id, s: projectPointToCurve(guide, p)?.s ?? 0 };
        case 'concentric-circles': {
            const ring = guide.rings.find(r => r.id === target.ringId);
            if (ring == null) return null;
            return { kind: 'guide-circle', guideId: guide.id, ringId: ring.id, angle: Math.atan2(p.y - guide.center.y, p.x - guide.center.x) };
        }
        case 'concentric-arcs': {
            const ring = guide.rings.find(r => r.id === target.ringId);
            if (ring == null) return null;
            const angle = clampAngleToArc(ring.startAngle, ring.endAngle, Math.atan2(p.y - guide.center.y, p.x - guide.center.x));
            return { kind: 'guide-circle', guideId: guide.id, ringId: ring.id, angle };
        }
        default: {
            const _exhaustive: never = guide;
            return _exhaustive;
        }
    }
}

// ---- 4. Narrow -------------------------------------------------------------

type Decisions = {
    arrayTop: (childId: TbcId, snap: ArrayTopSnap) => TopAttachSnap | null;
    pins: ReadonlyMap<TbcId, ReadonlyMap<number, TopAttachSnap>>;
    spaceBase: (b: WideBase, snap: ArraySpaceSnap) => Base;
};

/** Stitch `id` with every snap that names an array rewritten by the
 *  decisions, and its inverted pins applied: `t` itself when nothing changes. */
function narrowStitch(id: TbcId, t: WideTbc, decisions: Decisions): Tbc {
    const bases = t.bases.map((b, k) => narrowBase(id, k, b, decisions));
    const tops = t.tops.map(narrowTop);
    const basesSame = bases.every((b, k) => b === t.bases[k]);
    const topsSame = tops.every((top, k) => top === t.tops[k]);
    if (basesSame && topsSame && isNarrowTbc(t)) return t;
    return {
        ...t,
        bases: basesSame && t.bases.every(isNarrowBase) ? t.bases : bases,
        tops: topsSame && t.tops.every(isNarrowTop) ? t.tops : tops,
    };
}

/** Base `k` of stitch `id`: an inverted pin on a free base, a remapped
 *  `array-top`, a converted `array-space`, or let go (`array-base` on a base,
 *  which only a top can hold). `b` itself when none applies. */
function narrowBase(id: TbcId, k: number, b: WideBase, decisions: Decisions): Base {
    if (isNarrowBase(b)) {
        const pin = decisions.pins.get(id)?.get(k);
        return pin == null ? b : { ...b, snap: pin };
    }
    const snap = b.snap;
    // Unreachable: `isNarrowBase(b)` above already took these; this narrows the type.
    if (snap == null || isNarrowSnap(snap)) return { ...b, snap };
    switch (snap.kind) {
        case 'array-top':
            return { ...b, snap: decisions.arrayTop(id, snap) };
        case 'array-base':
            return { ...b, snap: null };
        case 'array-space':
            return decisions.spaceBase(b, snap);
        default: {
            const _exhaustive: never = snap;
            return _exhaustive;
        }
    }
}

/** A top pinned under an instance base lets go where it is: the pin was
 *  inverted onto the instance's base, or had nothing to invert onto. */
function narrowTop(top: WideTop): Top {
    if (isNarrowTop(top)) return top;
    const snap = top.snap;
    // Unreachable: `isNarrowTop(top)` above already took these; this narrows the type.
    if (snap == null || isNarrowTopSnap(snap)) return { ...top, snap };
    switch (snap.kind) {
        case 'array-base':
            return { ...top, snap: null };
        default: {
            const _exhaustive: never = snap;
            return _exhaustive;
        }
    }
}

// ---- 5. Vertical links -----------------------------------------------------

function isNarrowRef(ref: WideVerticalNodeRef): ref is VerticalNodeRef {
    return ref.kind !== 'array';
}

function isNarrowLink(link: WideVerticalLink): link is VerticalLink {
    return isNarrowRef(link.parent) && isNarrowRef(link.child);
}

/** The same key for a→b and b→a. */
const pairKey = (a: VerticalNodeRef, b: VerticalNodeRef): string => JSON.stringify([nodeRefKey(a), nodeRefKey(b)].sort(byId));

/** Every link with an `array` end pointed at that array's instance 0 (where
 *  the link was anchored), or dropped: when instance 0 has no stitch, when the
 *  link would join a node to itself, or when it would duplicate the node pair
 *  (either direction) of a link without array ends or of a smaller-id
 *  rewritten link. Rewritten links are decided in id order, so which one of
 *  two duplicates survives doesn't depend on map order. Links without array
 *  ends are kept as they are; the output keeps the input's order. */
function remapVerticalLinks(
    links: ReadonlyMap<VerticalLinkId, WideVerticalLink>,
    stitchExists: (id: TbcId) => boolean,
): Map<VerticalLinkId, VerticalLink> {
    const remapRef = (ref: WideVerticalNodeRef): VerticalNodeRef | null => {
        if (isNarrowRef(ref)) return ref;
        const id = legacyStitchId(ref.id, 0);
        return stitchExists(id) ? { kind: 'stitch', id } : null;
    };

    const linked = new Set<string>();
    for (const link of links.values()) {
        if (isNarrowLink(link)) linked.add(pairKey(link.parent, link.child));
    }
    const rewritten = new Map<VerticalLinkId, VerticalLink>();
    for (const id of [...links.keys()].sort(byId)) {
        const link = links.get(id)!;
        if (isNarrowLink(link)) continue;
        const parent = remapRef(link.parent);
        const child = remapRef(link.child);
        if (parent == null || child == null || nodeRefKey(parent) === nodeRefKey(child)) continue;
        const key = pairKey(parent, child);
        if (linked.has(key)) continue;
        linked.add(key);
        rewritten.set(id, { ...link, parent, child });
    }

    const out = new Map<VerticalLinkId, VerticalLink>();
    for (const [id, link] of links) {
        const next = isNarrowLink(link) ? link : rewritten.get(id);
        if (next != null) out.set(id, next);
    }
    return out;
}

// ---- 6. Turning chains -----------------------------------------------------

/** The turning chains with each array id replaced by the ids of its instances
 *  whose output stitch is chain-family (`canMarkTurningChain` would refuse the
 *  others), in index order, in the array id's place. That holds for an array
 *  the document still has (its instances by index) and for one converted
 *  earlier (`namesConvertedArray`: a late mark from an old peer), whose
 *  instances are the stitches still holding its derived ids, as `array-top`
 *  and vertical links find them once the array is gone. Any other id is kept,
 *  including one that names nothing: repair prunes it. */
function remapTurningChains(doc: LegacyDocMaps, stitches: ReadonlyMap<TbcId, Tbc>): Set<string> {
    const out = new Set<string>();
    const addChainFamily = (stitchId: TbcId): void => {
        const s = stitches.get(stitchId);
        if (s != null && isChainSpaceType(s.type)) out.add(stitchId);
    };
    for (const id of doc.turningChains) {
        const a = doc.arrayTbcs.get(id);
        if (a == null && namesConvertedArray(id, (stitchId) => stitches.has(stitchId))) {
            convertedInstanceIds(id, stitches).forEach(addChainFamily);
            continue;
        }
        // Kept when it names no array, or (both at once) also a stitch.
        if (a == null || stitches.has(id)) out.add(id);
        if (a == null) continue;
        for (let i = 0; i < legacyInstanceCount(a); i++) addChainFamily(legacyStitchId(id, i));
    }
    return out;
}

/** The stitches exploded from array `arrayId` that `stitches` still holds:
 *  every id `legacyStitchId(arrayId, i)`, by index. The array is gone, so its
 *  count isn't known, and instances may have been deleted since. */
function convertedInstanceIds(arrayId: string, stitches: ReadonlyMap<TbcId, Tbc>): TbcId[] {
    // What every instance id starts with (instance 0's, less its index); the
    // check below keeps only ids `legacyStitchId` itself makes.
    const prefix = legacyStitchId(arrayId, 0).slice(0, -1);
    const found: Array<{ index: number; id: TbcId }> = [];
    for (const id of stitches.keys()) {
        if (!id.startsWith(prefix)) continue;
        const rest = id.slice(prefix.length);
        if (!/^(0|[1-9]\d*)$/.test(rest)) continue;
        const index = Number(rest);
        if (legacyStitchId(arrayId, index) === id) found.push({ index, id });
    }
    return found.sort((x, y) => x.index - y.index).map(f => f.id);
}
